import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Clock, 
  MapPin, 
  CheckCircle, 
  XCircle, 
  LogIn, 
  LogOut,
  AlertTriangle,
  Navigation,
  Loader2,
  MessageSquare
} from "lucide-react";
import { toast } from "sonner";
import LeaveRequestModal from "@/components/notifications/LeaveRequestModal";

const API_URL = '';

export default function TeacherSignInPage() {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [signInLocationStatus, setSignInLocationStatus] = useState(null);
  const [lateReason, setLateReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const isLateNow = () => new Date(new Date().getTime() + (11 * 60 * 60 * 1000)).getUTCHours() >= 8;
  const finalLateReason = lateReason === 'Other' ? otherReason.trim() : lateReason;

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchStatus();
    
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/teacher-attendance/my-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.error("Error fetching status:", error);
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      setGettingLocation(true);
      setLocationError(null);
      
      if (!navigator.geolocation) {
        setLocationError("Geolocation is not supported by your browser");
        setGettingLocation(false);
        reject(new Error("Geolocation not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setLocation(loc);
          setGettingLocation(false);
          resolve(loc);
        },
        (error) => {
          let errorMsg = "Unable to get location";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMsg = "Please allow location access to sign in";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = "Location information unavailable";
              break;
            case error.TIMEOUT:
              errorMsg = "Location request timed out";
              break;
          }
          setLocationError(errorMsg);
          setGettingLocation(false);
          reject(new Error(errorMsg));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  const handleSignIn = async () => {
    if (isLateNow() && !finalLateReason) {
      toast.error(lateReason === 'Other' ? "Please explain your reason for arriving late" : "Please select a reason for arriving late");
      return;
    }
    setSigningIn(true);
    try {
      // Try to get location, but don't block sign-in if it fails
      let loc = { latitude: 0, longitude: 0 };
      try {
        loc = await getLocation();
      } catch (locError) {
        // Location permission denied - continue with network-based sign-in
        console.log("Location not available, using network-based sign-in");
        setLocationError(null); // Clear error since we'll use network detection
      }
      
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/teacher-attendance/sign-in`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          latitude: loc.latitude,
          longitude: loc.longitude,
          late_reason: finalLateReason || null
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        // Show appropriate message based on network status
        if (data.is_on_school_network) {
          toast.success("Signed in successfully! (On-Site Network)");
        } else {
          toast.success("Signed in successfully!");
        }
        fetchStatus();
        
        // Check if teacher is off campus or nearby - show leave request modal
        if (data.location_status === 'off_campus' || data.location_status === 'nearby') {
          setSignInLocationStatus(data.location_status);
          setShowLeaveModal(true);
        }
      } else {
        toast.error(data.detail || "Failed to sign in");
      }
    } catch (error) {
      toast.error(error.message || "Failed to sign in");
    } finally {
      setSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/teacher-attendance/sign-out`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Signed out! Hours worked: ${data.hours_worked}`);
        fetchStatus();
      } else {
        toast.error(data.detail || "Failed to sign out");
      }
    } catch (error) {
      toast.error("Failed to sign out");
    } finally {
      setSigningOut(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "--:--";
    const date = new Date(isoString);
    // Convert to Vanuatu time (UTC+11)
    const vanuatuTime = new Date(date.getTime() + (11 * 60 * 60 * 1000));
    return vanuatuTime.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getLocationStatusBadge = (locationStatus) => {
    switch (locationStatus) {
      case "at_school":
        return <Badge className="bg-green-100 text-green-800"><MapPin className="w-3 h-3 mr-1" /> At School</Badge>;
      case "nearby":
        return <Badge className="bg-yellow-100 text-yellow-800"><MapPin className="w-3 h-3 mr-1" /> Nearby</Badge>;
      case "off_campus":
        return <Badge className="bg-red-100 text-red-800"><AlertTriangle className="w-3 h-3 mr-1" /> Off Campus</Badge>;
      default:
        return null;
    }
  };

  const getVerificationBadge = (verificationStatus) => {
    switch (verificationStatus) {
      case "verified":
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" /> Verified</Badge>;
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" /> Pending Verification</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return null;
    }
  };

  // Get Vanuatu time
  const vanuatuTime = new Date(currentTime.getTime() + (11 * 60 * 60 * 1000));
  const timeString = vanuatuTime.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    second: '2-digit',
    hour12: true 
  });
  const dateString = vanuatuTime.toLocaleDateString('en-US', { 
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto" data-testid="teacher-signin-page">
      {/* Current Time Display */}
      <Card className="mb-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <CardContent className="py-8 text-center">
          <div className="text-5xl font-bold mb-2" data-testid="current-time">
            {timeString}
          </div>
          <div className="text-blue-100">
            {dateString}
          </div>
          <div className="text-blue-200 text-sm mt-1">
            Vanuatu Time (UTC+11)
          </div>
        </CardContent>
      </Card>

      {/* User Info */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Welcome, {user?.name || "Teacher"}</CardTitle>
          <CardDescription>
            Daily Attendance Sign-In/Sign-Out
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Status Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Today's Attendance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status?.signed_in ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Signed In At</p>
                  <p className="text-xl font-semibold text-green-700">
                    {formatTime(status.sign_in_time)}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>

              <div className="flex flex-wrap gap-2">
                {getLocationStatusBadge(status.location_status)}
                {status.distance_meters && (
                  <Badge variant="outline">
                    <Navigation className="w-3 h-3 mr-1" />
                    {status.distance_meters}m from school
                  </Badge>
                )}
                {status.is_late && (
                  <Badge className="bg-orange-100 text-orange-800">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Late
                  </Badge>
                )}
                {status.notes && status.notes.startsWith('Late reason:') && (
                  <Badge variant="outline" className="text-orange-700 border-orange-300">
                    <MessageSquare className="w-3 h-3 mr-1" />
                    {status.notes.replace('Late reason: ', '')}
                  </Badge>
                )}
                {getVerificationBadge(status.verification_status)}
              </div>

              {status.signed_out ? (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Signed Out At</p>
                  <p className="text-xl font-semibold">
                    {formatTime(status.sign_out_time)}
                  </p>
                </div>
              ) : (
                <Button
                  onClick={handleSignOut}
                  disabled={signingOut}
                  className="w-full bg-red-600 hover:bg-red-700"
                  size="lg"
                  data-testid="sign-out-btn"
                >
                  {signingOut ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Signing Out...
                    </>
                  ) : (
                    <>
                      <LogOut className="w-5 h-5 mr-2" />
                      Sign Out
                    </>
                  )}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <XCircle className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-600">Not signed in yet</p>
                <p className="text-sm text-gray-500">
                  Sign in before 8:00 AM to avoid being marked late
                </p>
              </div>

              {locationError && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-red-600 text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {locationError}
                  </p>
                </div>
              )}

              {/* Late Reason - required if current time is past 8 AM Vanuatu time */}
              {isLateNow() && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-3" data-testid="late-reason-section">
                    <div className="flex items-center gap-2 text-orange-700">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm font-semibold">You are signing in after 8:00 AM</span>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-orange-800">Reason for late arrival <span className="text-red-600">*</span></label>
                      <Select value={lateReason} onValueChange={setLateReason}>
                        <SelectTrigger className="w-full bg-white" data-testid="late-reason-select">
                          <SelectValue placeholder="Select a reason..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Traffic / transport delay">Traffic / transport delay</SelectItem>
                          <SelectItem value="Sick - feeling unwell">Sick - feeling unwell</SelectItem>
                          <SelectItem value="Family emergency">Family emergency</SelectItem>
                          <SelectItem value="Traditional / cultural obligation">Traditional / cultural obligation</SelectItem>
                          <SelectItem value="Church commitment">Church commitment</SelectItem>
                          <SelectItem value="Bad weather / road conditions">Bad weather / road conditions</SelectItem>
                          <SelectItem value="Medical appointment">Medical appointment</SelectItem>
                          <SelectItem value="School business / official duty">School business / official duty</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {lateReason === 'Other' && (
                        <Textarea
                          placeholder="Please explain briefly..."
                          className="bg-white text-sm"
                          value={otherReason}
                          onChange={(e) => setOtherReason(e.target.value)}
                          maxLength={200}
                          data-testid="late-reason-other"
                        />
                      )}
                      <p className="text-xs text-orange-700">Your principal will see this reason next to your sign-in time.</p>
                    </div>
                  </div>
              )}

              <Button
                onClick={handleSignIn}
                disabled={signingIn || gettingLocation}
                className="w-full bg-green-600 hover:bg-green-700"
                size="lg"
                data-testid="sign-in-btn"
              >
                {signingIn || gettingLocation ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {gettingLocation ? "Getting Location..." : "Signing In..."}
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5 mr-2" />
                    Sign In Now
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-gray-500">
                <MapPin className="w-3 h-3 inline mr-1" />
                Your location will be recorded for verification
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <h4 className="font-medium text-blue-800 mb-2">📋 Guidelines</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Sign in when you arrive at school</li>
            <li>• Location is recorded to verify you're at school</li>
            <li>• Principal will verify your attendance</li>
            <li>• Sign out when you leave for the day</li>
            <li>• Late = After 8:00 AM</li>
          </ul>
        </CardContent>
      </Card>

      {/* Leave Request Modal - shown after sign-in if off campus */}
      <LeaveRequestModal
        open={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        date={new Date().toISOString().split('T')[0]}
        locationStatus={signInLocationStatus}
      />
    </div>
  );
}
